import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, CheckCircle2, TrendingUp, Users, BarChart2, Zap, Brain, Shield } from 'lucide-react';

const SportsEntertainment = () => {
  const useCases = [
    {
      icon: Brain,
      title: 'Sports Performance & Predictive Analytics',
      description: 'ML models that analyse athlete biometrics, tracking data, and historical performance to predict outcomes, optimise training loads, and reduce injury risk.',
      outcomes: ['Injury risk reduction by 30%+', 'Data-driven team selection', 'Real-time in-match analytics'],
    },
    {
      icon: TrendingUp,
      title: 'Sports Betting & Prediction Engines',
      description: 'Statistical models and real-time data pipelines that power odds generation, market making, and live betting platforms with sub-second latency.',
      outcomes: ['Real-time odds calculation', 'In-play event detection', 'Multi-sport prediction models'],
    },
    {
      icon: Users,
      title: 'Fan Engagement & Personalisation',
      description: 'AI-driven personalisation engines that deliver hyper-relevant content, offers, and experiences across digital platforms — increasing fan lifetime value.',
      outcomes: ['3x improvement in content engagement', 'Personalised push notifications', 'Churn prediction and retention campaigns'],
    },
    {
      icon: BarChart2,
      title: 'Ticketing & Revenue Optimisation',
      description: 'Dynamic pricing models, demand forecasting, and seat-yield management systems that maximise gate revenue without alienating fans.',
      outcomes: ['15–25% increase in ticket revenue', 'Demand-based dynamic pricing', 'Inventory optimisation across events'],
    },
    {
      icon: Zap,
      title: 'Media & Content Intelligence',
      description: 'AI video clipping, highlight generation, and content tagging that dramatically reduces production time for sports media and streaming platforms.',
      outcomes: ['Automated highlight generation', '10x faster content clipping', 'AI-powered metadata tagging'],
    },
    {
      icon: Shield,
      title: 'Sponsorship & Commercial Analytics',
      description: 'Computer vision and NLP tools that measure sponsor brand exposure across broadcasts, social, and digital — proving ROI on commercial partnerships.',
      outcomes: ['Automated sponsor exposure measurement', 'Multi-platform brand reach analytics', 'ROI reporting for commercial teams'],
    },
  ];

  const caseStudies = [
    {
      title: 'Sports Prediction & Analytics Platform',
      result: 'Built a real-time sports prediction engine processing 50,000+ events per day with 78% accuracy across 12 leagues.',
      link: '/case-studies/sports-prediction',
    },
    {
      title: 'Marketing Automation for Entertainment',
      result: 'Automated fan re-engagement campaigns resulting in 40% improvement in ticket renewal rates.',
      link: '/case-studies/marketing-automation',
    },
  ];

  const techStack = [
    'Python / TensorFlow / PyTorch', 'Apache Kafka for real-time data', 'Azure ML & Databricks',
    'Computer Vision (OpenCV, YOLO)', 'GPT-4 for content generation', 'Opta / StatsBomb data feeds',
    'Power BI fan analytics dashboards', 'AWS Kinesis for live data streams',
  ];

  return (
    <>
      <Helmet>
        <title>AI for Sports & Entertainment | Performance Analytics, Fan Engagement & Betting AI | Agility</title>
        <meta
          name="description"
          content="AI solutions for sports and entertainment organisations. Performance analytics, sports prediction, fan personalisation, dynamic ticketing, media intelligence, and sponsorship analytics. Production systems in 3–8 weeks."
        />
        <meta
          name="keywords"
          content="sports AI, sports analytics AI, fan engagement AI, sports betting AI, entertainment AI, sports performance analytics, ticketing AI, sponsorship analytics AI"
        />
        <link rel="canonical" href="https://agility.ai/industries/sports-entertainment" />
        <meta property="og:title" content="AI for Sports & Entertainment | Agility" />
        <meta property="og:description" content="AI solutions for sports and entertainment: performance analytics, fan engagement, and commercial intelligence." />
        <meta property="og:type" content="website" />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-40 pb-24 bg-black overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 to-black pointer-events-none" />
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl">
            <div className="inline-flex items-center bg-teal-500/10 border border-teal-500/20 rounded-full px-4 py-2 mb-6">
              <span className="text-teal-400 text-sm font-medium">Industry Solution</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              AI for <span className="text-teal-400">Sports & Entertainment</span>
            </h1>
            <p className="text-xl text-gray-300 mb-8 max-w-2xl leading-relaxed">
              We build AI systems that drive performance on the field, deepen fan connections, and unlock commercial value across the sports and entertainment ecosystem.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/contact"
                className="inline-flex items-center bg-teal-500 text-white px-8 py-4 rounded-md hover:bg-teal-400 transition-colors font-semibold text-lg"
              >
                Talk to a Sports AI Specialist <ArrowRight className="ml-2" size={20} />
              </a>
              <a
                href="/case-studies"
                className="inline-flex items-center border border-gray-600 text-white px-8 py-4 rounded-md hover:border-teal-400 hover:text-teal-400 transition-colors font-semibold text-lg"
              >
                View Case Studies
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">AI Use Cases for Sports & Entertainment</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              From athlete performance to fan monetisation — AI built for the world of sport and live entertainment.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => {
              const Icon = useCase.icon;
              return (
                <div key={index} className="bg-gray-900 rounded-xl p-6 border border-gray-800 hover:border-teal-500/40 transition-colors">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-teal-400" size={24} />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{useCase.title}</h3>
                  <p className="text-gray-400 mb-4 text-sm leading-relaxed">{useCase.description}</p>
                  <ul className="space-y-2">
                    {useCase.outcomes.map((outcome, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle2 className="text-teal-400 mt-0.5 flex-shrink-0" size={14} />
                        {outcome}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">Sports & Entertainment Case Studies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {caseStudies.map((cs, index) => (
              <a
                key={index}
                href={cs.link}
                className="block bg-gray-900 rounded-xl p-8 border border-gray-800 hover:border-teal-500/40 transition-colors group"
              >
                <h3 className="text-xl font-semibold text-white mb-3 group-hover:text-teal-400 transition-colors">{cs.title}</h3>
                <p className="text-gray-400 mb-4 text-sm">{cs.result}</p>
                <span className="text-teal-400 text-sm font-medium inline-flex items-center gap-1">
                  Read case study <ArrowRight size={14} />
                </span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="py-24 bg-gray-950">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-8">Technology Stack</h2>
            <div className="flex flex-wrap gap-3 justify-center">
              {techStack.map((tech, index) => (
                <span key={index} className="bg-gray-800 text-gray-300 px-4 py-2 rounded-full text-sm border border-gray-700">
                  {tech}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-teal-600">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to win with AI in sports and entertainment?</h2>
          <p className="text-teal-100 text-lg mb-8 max-w-2xl mx-auto">
            Production-ready AI in 3–8 weeks. Let's map your highest-impact opportunity today.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center bg-white text-teal-600 px-8 py-4 rounded-md hover:bg-gray-100 transition-colors font-semibold text-lg"
          >
            Book a Free Discovery Call <ArrowRight className="ml-2" size={20} />
          </a>
        </div>
      </section>
    </>
  );
};

export default SportsEntertainment;
